//
// Copyright 2025 DXOS.org
//

// @import-as-namespace

import * as Effect from 'effect/Effect';
import * as Function from 'effect/Function';
import * as Option from 'effect/Option';
import * as String from 'effect/String';

import {
  Annotation,
  type Database,
  Entity,
  Filter,
  Format,
  JsonSchema,
  Obj,
  Query,
  Ref,
  type Registry,
  Scope,
  Type,
  View,
} from '@dxos/echo';
import { TypeEnum } from '@dxos/echo/Format';
import * as EffectEx from '@dxos/effect/EffectEx';
import * as SchemaAST from '@dxos/effect/SchemaAST';
import * as SchemaEx from '@dxos/effect/SchemaEx';
import { invariant } from '@dxos/invariant';

import { ProjectionModel, createEchoChangeCallback } from '../projection/index.ts';
import { createDefaultSchema, getSchema } from '../util/index.ts';

type MakeProps = {
  name?: string;
  query: Query.Any;
  queryRaw?: string;
  // TODO(wittjosiah): Revisit this and try to unify this. Maybe always expect Type.AnyEntity since it can be created from JsonSchema anyways.
  jsonSchema: JsonSchema.JsonSchema; // Base schema.
  /** Persisted `Type.Type` entity backing `jsonSchema`, when one exists; enables `Type.update` on schema edits. */
  type?: Type.AnyEntity;
  overrideSchema?: JsonSchema.JsonSchema; // Override schema.
  fields?: string[];
  pivotFieldName?: string;
};

/**
 * Create view from provided schema.
 */
export const make = ({
  query,
  queryRaw,
  jsonSchema,
  type,
  overrideSchema,
  fields,
  pivotFieldName,
}: MakeProps): View.View => {
  const view = Obj.make(View.View, {
    query: { raw: queryRaw, ast: query.ast },
    projection: {
      schema: overrideSchema,
      fields: [],
    },
  });

  const projection = new ProjectionModel({
    view,
    baseSchema: jsonSchema,
    change: createEchoChangeCallback(view, type),
  });
  projection.normalizeView();
  const effectSchema = JsonSchema.toEffectSchema(jsonSchema);
  const properties = SchemaEx.getProperties(effectSchema.ast);
  for (const property of properties) {
    const name = property.name.toString() as SchemaEx.JsonProp;
    const include = fields ? fields.includes(name) : name !== 'id';
    if (!include) {
      continue;
    }

    const format = Format.FormatAnnotation.getFromAst(property.type);
    // Omit objects from initial projection as they are difficult to handle automatically.
    if ((SchemaEx.isNestedType(property.type) && Option.isNone(format)) || SchemaEx.isArrayType(property.type)) {
      continue;
    }

    projection.showFieldProjection(name);
  }

  // Sort fields to match the order in the params.
  if (fields) {
    Obj.update(view, (view) => {
      (view.projection.fields as Obj.Mutable<View.Projection>['fields']).sort((a, b) => {
        const indexA = fields.indexOf(a.path);
        const indexB = fields.indexOf(b.path);
        return indexA - indexB;
      });
    });
  }

  if (pivotFieldName) {
    const fieldId = projection.getFieldId(pivotFieldName);
    if (fieldId) {
      Obj.update(view, (view) => {
        view.projection.pivotFieldId = fieldId;
      });
    }
  }

  return view;
};

export type MakeWithReferencesProps = MakeProps & {
  registry?: Registry.Registry;
};

/**
 * Create view from provided schema with references for fields that are references.
 * Referenced schemas are resolved in the provided registries.
 */
export const makeWithReferences = async ({
  query,
  queryRaw,
  jsonSchema,
  type,
  overrideSchema,
  fields,
  pivotFieldName,
  registry,
}: MakeWithReferencesProps): Promise<View.View> => {
  const view = make({
    query,
    queryRaw,
    jsonSchema,
    type,
    overrideSchema,
    fields,
    pivotFieldName,
  });

  const projection = new ProjectionModel({
    view,
    baseSchema: jsonSchema,
    change: createEchoChangeCallback(view, type),
  });
  const effectSchema = JsonSchema.toEffectSchema(jsonSchema);
  const properties = SchemaEx.getProperties(effectSchema.ast);
  for (const property of properties) {
    const name = property.name.toString() as SchemaEx.JsonProp;
    const include = fields ? fields.includes(name) : name !== 'id';
    if (!include) {
      continue;
    }

    if (!Ref.isRefType(property.type)) {
      continue;
    }

    projection.showFieldProjection(name);

    await Effect.gen(function* () {
      // v4's `Effect.gen` no longer yields an `Option` directly; absence becomes a
      // `NoSuchElementError` failure, which the recovery below already handles.
      // The target is read through `Ref.getReferenceTarget` rather than the typed annotation: this
      // schema was rebuilt from JSON, which carries the reference only as encoded `$ref` keys.
      const referenceDXN = yield* Effect.fromNullishOr(Ref.getReferenceTarget(property.type));

      const referenceSchema = yield* Effect.tryPromise(() => getSchema(referenceDXN, registry));

      const referencePath = yield* Effect.fromOption(
        Function.pipe(
          Option.fromNullishOr(referenceSchema),
          Option.map((schema) => Type.getSchema(schema)),
          Option.flatMap((schema) => Annotation.LabelAnnotation.get(schema)),
          Option.flatMap((labels) => (labels.length > 0 ? Option.some(labels[0]) : Option.none())),
        ),
      );

      if (referenceSchema && referencePath) {
        const fieldId = yield* Effect.fromNullishOr(view.projection.fields?.find((f) => f.path === property.name)?.id);
        const title =
          SchemaEx.getAnnotation<string>(SchemaAST.TitleAnnotationId)(property.type) ?? String.capitalize(name);
        projection.setFieldProjection({
          field: {
            id: fieldId,
            path: property.name as SchemaEx.JsonPath,
            referencePath: referencePath as SchemaEx.JsonPath,
          },
          props: {
            property: property.name as SchemaEx.JsonProp,
            type: TypeEnum.Ref,
            format: Format.TypeFormat.Ref,
            referenceSchema: Type.getTypename(referenceSchema),
            title,
          },
        });
      }
    }).pipe(
      Effect.catchIf(
        (error) => error._tag === 'NoSuchElementError',
        () => Effect.succeed('Recovering from NoSuchElementError'),
      ),
      EffectEx.runAndForwardErrors,
    );
  }

  return view;
};

export type MakeFromDatabaseProps = Omit<MakeWithReferencesProps, 'query' | 'queryRaw' | 'jsonSchema' | 'registry'> & {
  db: Database.Database;
  typename?: string;
  createInitial?: number;
};

/**
 * Create view from a schema in provided space or client.
 */
export const makeFromDatabase = async ({
  db,
  typename,
  createInitial = 1,
  ...props
}: MakeFromDatabaseProps): Promise<{ jsonSchema: JsonSchema.JsonSchema; view: View.View }> => {
  if (!typename) {
    const type = await db.addType(createDefaultSchema());
    // `db.addType` returns a persisted `Type.Type` entity; its typename lives in the
    // type metadata, so read it via `Type.getTypename` rather than a `.typename` prop.
    typename = Type.getTypename(type);
  } else {
    createInitial = 0;
  }

  const allTypes = await db.query(Query.select(Filter.type(Type.Type)).from(Scope.space(), Scope.registry())).run();
  // `typename` is matched against both the bare typename and the type URI: callers pass the bare
  // typename, but type-picker forms (TypeOptions) supply the versioned type URI as the field value.
  const type = allTypes.find((t) => Type.getTypename(t) === typename || Type.getURI(t) === typename);
  invariant(type, `Type not found: ${typename}`);
  // `type` is a `Type.Type` entity (type-kind brand). The kind it *describes*
  // lives in the `TypeAnnotation` on the rebuilt Effect Schema — read it via
  // `Entity.getKind` rather than the entity-level `Type.isObject` guard.
  const effectSchema = Type.getSchema(type);
  invariant(Entity.getKind(effectSchema) === Entity.Kind.Object, `Schema is not an object schema: ${typename}`);
  const jsonSchema = type.jsonSchema;

  Array.from({ length: createInitial }).forEach(() => {
    db.add(Obj.make(Type.assertObject(type), {}));
  });

  return {
    jsonSchema,
    view: await makeWithReferences({
      ...props,
      // Objects created from DB type entities are stamped with the entity's echo:/@:<id>
      // URI, not the typename DXN; Filter.type matches that URI whereas Filter.typename misses it.
      query: Query.select(Filter.type(type)),
      jsonSchema,
      type,
      registry: db.graph.registry,
    }),
  };
};
